// src/components/Dashboard/LiveRideCard.jsx
import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, Truck, Zap } from 'lucide-react'; // Import Info and Truck icons
import React from 'react';
import { cn } from '../../lib/utils'; // Assuming this path
import Card from '../ui/card.js';

// Framer Motion variants
const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const LiveRideCard = ({ ride, onClick, onShowEmployeeInfo, isSelected }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'in progress':
                return 'text-black';
            case 'not started':
            case 'not started':
                return 'text-black';
            case 'completed':
                return 'text-black';
            default:
                return 'text-black';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'in progress':
                return <Zap className="w-4 h-4 text-black" />;
            case 'not started':
                return <AlertTriangle className="w-4 h-4 text-black" />;
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-black" />;
            default:
                return null;
        }
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return 'N/A';
        }
    };

    return (
        <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <Card
                variant="light"
                className={cn(
                    'relative p-4 flex flex-col justify-between transition-all duration-300 ease-in-out',
                    isSelected ? 'border-2 border-white' : 'border border-black/20 hover:opacity-90',
                    'min-w-[340px] max-w-sm flex-shrink-0 cursor-pointer' // Ensure it's not too wide and allows horizontal scrolling
                )}
                onClick={() => onClick(ride.id)} // Always allow click to select/deselect
            >
            <div className="flex justify-between items-center mb-2">
                <Typography variant="h6" className="text-black font-bold flex items-center gap-2">
                    <Truck className="w-5 h-5 text-black" />
                    Ride {ride.id.slice(-4)}
                </Typography>
                <div className={cn('text-sm font-semibold flex items-center gap-1', getStatusColor(ride.status))}>
                    {getStatusIcon(ride.status)}
                    {ride.status.charAt(0).toUpperCase() + ride.status.slice(1)}
                </div>
            </div>
            <Typography variant="body1" className="text-black">
                <span className="font-medium text-black">From:</span> {ride.startSiteName}
            </Typography>
            {ride.fromSupervisorName && (
                <Typography variant="body2" className="text-gray-600 text-sm">
                    <span className="font-medium">Assigned Employee:</span> {ride.fromSupervisorName}
                </Typography>
            )}
            <Typography variant="body1" className="text-black">
                <span className="font-medium text-black">To:</span> {ride.endSiteName}
            </Typography>
            {ride.toSupervisorName && (
                <Typography variant="body2" className="text-gray-600 text-sm">
                    <span className="font-medium">Assigned Employee:</span> {ride.toSupervisorName}
                </Typography>
            )}
            {(ride.ride_start_timestamp || ride.ride_end_timestamp) && (
                <div className="mt-2 pt-2 border-t border-black/10">
                    <Typography variant="body2" className="text-black text-sm">
                        <span className="font-medium">Start:</span> {formatTimestamp(ride.ride_start_timestamp)}
                    </Typography>
                    <Typography variant="body2" className="text-black text-sm">
                        <span className="font-medium">End:</span> {formatTimestamp(ride.ride_end_timestamp)}
                    </Typography>
                </div>
            )}

            {/* Commented out - Tracking and Info buttons */}
            {/* <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/20">
                <Button
                    onClick={() => onClick(ride.id)}
                    className={cn("px-3 py-1 text-sm rounded-full", isSelected ? "bg-blue-600 text-white" : "bg-gray-700/50 text-gray-200 hover:bg-gray-600/50")}
                >
                    {isSelected ? "Tracking" : "Track Ride"}
                </Button>
                <Button
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent card selection when clicking info button
                        if (onShowEmployeeInfo) {
                            onShowEmployeeInfo(); // Trigger showing the employee info table
                        }
                    }}
                    className="px-3 py-1 text-sm rounded-full bg-blue-500/50 text-white hover:bg-blue-600/50"
                    title="View Employee Info"
                >
                    <Info className="w-4 h-4 mr-1" />
                    Info
                </Button>
            </div> */}
            </Card>
        </motion.div>
    );
};

export default LiveRideCard;
